import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SelectItemV2, UNIT_DEFAULTS } from '@schenck/rotec-components';
import {
  QualityGradeTypes,
  RotorTypeSelections,
  TolerancePlanePositions,
} from 'src/utils/enums/rotor-type.enum';
import { RotorTypeConfigurationModel } from 'src/utils/interfaces/rotor-type.interfaces';
import { valueRangeValidator } from '../../rotor-type-details.helper';

export const TOLERANCE_TABS: string[] = ['User Defined', 'DIN ISO 21940-11 Calculation'];

export const TOLERANCES_UNITS: (SelectItemV2 & { unitValue: number })[] = [
  { name: 'Unbalance Units', value: 'Unbalance', unitValue: 1 },
  { name: 'Mass Units', value: 'Mass', unitValue: 2 },
];

export const TOLERANCES_CALCULATIONS: SelectItemV2[] = [
  { name: 'Show Correction', value: true },
  { name: "Don't Show Correction", value: false },
];

export const TOLERANCE_PLANE_POSITIONS: SelectItemV2<number>[] = [
  { name: 'Measuring Planes', value: TolerancePlanePositions.Measuring },
  { name: 'Correction Planes', value: TolerancePlanePositions.Correction },
  // { name: 'Custom Planes', value: TolerancePlanePositions.Custom }, // Commented out due to scope changes for Ewok
];

export const USER_DEFINED_FORM = {
  selectedDynamicUnit: new UntypedFormControl(TOLERANCES_UNITS[0]),
  selectedStaticUnit: new UntypedFormControl(TOLERANCES_UNITS[0]),
  selectedCoupleUnit: new UntypedFormControl(TOLERANCES_UNITS[0]),
  selectedCalculation: new UntypedFormControl(TOLERANCES_CALCULATIONS[0]),
  staticTolerance: new UntypedFormControl({
    name: 'staticTolerance',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  }),
  coupleTolerance: new UntypedFormControl({
    name: 'coupleTolerance',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  }),
  dynamicTolerance1: new UntypedFormControl({
    name: 'dynamicTolerance1',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  }),
  dynamicTolerance2: new UntypedFormControl({
    name: 'dynamicTolerance2',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  }),
  tolerancePlane1Distance: new UntypedFormGroup({
    name: new UntypedFormControl('tolerancePlane1Distance'),
    value: new UntypedFormControl(null),
    unit: new UntypedFormControl(UNIT_DEFAULTS.length),
  }),
  tolerancePlane2Distance: new UntypedFormGroup({
    name: new UntypedFormControl('tolerancePlane2Distance'),
    value: new UntypedFormControl(null),
    unit: new UntypedFormControl(UNIT_DEFAULTS.length),
  }),

  selectedTolerancePlane: new UntypedFormControl(TOLERANCE_PLANE_POSITIONS[1]),
};

export const ISO_CALCULATION_FORM = {
  selectedUnbalanceUnit: new UntypedFormControl(null),
  selectedCalculation: new UntypedFormControl(null),
  rotorMass: new UntypedFormControl({
    name: 'rotorMass',
    value: null,
    unit: null,
  }),
  nominalSpeed: new UntypedFormControl({
    name: 'nominalSpeed',
    value: null,
    unit: null,
  }),
  balancingQualityGrade: new UntypedFormControl({
    name: 'balancingQualityGrade',
    value: null,
    unit: null,
  }),
  selectedStaticUnit: new UntypedFormControl(null),
  staticTolerance: new UntypedFormControl({
    name: 'staticTolerance',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  }),
  selectedDynamicUnit: new UntypedFormControl(null),
  dynamicTolerance1: new UntypedFormControl({
    name: 'dynamicTolerance1',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  }),
  dynamicTolerance2: new UntypedFormControl({
    name: 'dynamicTolerance2',
    value: null,
    unit: UNIT_DEFAULTS.unbalance,
  }),
  centerGravity1: new UntypedFormControl({
    name: 'centerGravity1',
    value: null,
    unit: UNIT_DEFAULTS.length,
  }),
  centerGravity2: new UntypedFormControl({
    name: 'centerGravity2',
    value: null,
    unit: UNIT_DEFAULTS.length,
  }),
  centerGravity: new UntypedFormControl({
    name: 'centerGravity',
    value: null,
    unit: UNIT_DEFAULTS.length,
  }),
  rollerBedDistance: new UntypedFormControl({
    name: 'rollerBedDistance',
    value: null,
    unit: UNIT_DEFAULTS.length,
  }),
  deviation: new UntypedFormControl(0, valueRangeValidator({ min: -25, max: 35 })),
  tolerancePlane1Distance: new UntypedFormGroup({
    name: new UntypedFormControl('tolerancePlane1Distance'),
    value: new UntypedFormControl(null),
    unit: new UntypedFormControl(UNIT_DEFAULTS.length),
  }),
  tolerancePlane2Distance: new UntypedFormGroup({
    name: new UntypedFormControl('tolerancePlane2Distance'),
    value: new UntypedFormControl(null),
    unit: new UntypedFormControl(UNIT_DEFAULTS.length),
  }),
  selectedTolerancePlane: new UntypedFormControl(TOLERANCE_PLANE_POSITIONS[1]),
};

export const getToleranceConfiguration = (
  mode: 'user-defined' | 'iso-calculation',
  laser: 'NoLaser' | 'Laser'
): RotorTypeConfigurationModel[] => {
  return [
    {
      title: 'static',
      value: RotorTypeSelections.Static,
      imageUrl: `./assets/img/tolerances/${mode}/Static/${laser}/Static.png`,
      class: 'static',
    },
    {
      title: 'centered',
      value: RotorTypeSelections.Centered,
      imageOptions: [
        {
          url: `./assets/img/tolerances/${mode}/Centered/${laser}/R1EqualR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/Centered/${laser}/R1SmallerR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/Centered/${laser}/R1LargerR2.png`,
        },
      ],
      class: 'centered',
    },
    {
      title: 'centerRight',
      value: RotorTypeSelections.CenterRight,
      imageOptions: [
        {
          url: `./assets/img/tolerances/${mode}/CenterRight/${laser}/R1EqualR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/CenterRight/${laser}/R1SmallerR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/CenterRight/${laser}/R1LargerR2.png`,
        },
      ],
      class: 'center-right',
    },
    {
      title: 'overhungRight',
      value: RotorTypeSelections.OverhungRight,
      imageOptions: [
        {
          url: `./assets/img/tolerances/${mode}/OverhungRight/${laser}/R1EqualR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/OverhungRight/${laser}/R1SmallerR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/OverhungRight/${laser}/R1LargerR2.png`,
        },
      ],
      class: 'overhung-right',
    },
    {
      title: 'polar',
      value: RotorTypeSelections.Polar,
      imageOptions: [
        {
          url: `./assets/img/tolerances/${mode}/Polar/${laser}/R1EqualR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/Polar/${laser}/R1SmallerR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/Polar/${laser}/R1LargerR2.png`,
        },
      ],
      class: 'polar',
    },
    {
      title: 'centerLeft',
      value: RotorTypeSelections.CenterLeft,
      imageOptions: [
        {
          url: `./assets/img/tolerances/${mode}/CenterLeft/${laser}/R1EqualR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/CenterLeft/${laser}/R1SmallerR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/CenterLeft/${laser}/R1LargerR2.png`,
        },
      ],
      class: 'center-left',
    },
    {
      title: 'overhungLeft',
      value: RotorTypeSelections.OverhungLeft,
      imageOptions: [
        {
          url: `./assets/img/tolerances/${mode}/OverhungLeft/${laser}/R1EqualR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/OverhungLeft/${laser}/R1SmallerR2.png`,
        },
        {
          url: `./assets/img/tolerances/${mode}/OverhungLeft/${laser}/R1LargerR2.png`,
        },
      ],
      class: 'overhung-left',
    },
  ];
};

export const BALANCING_QUALITY_GRADE: SelectItemV2<QualityGradeTypes>[] = [
  {
    name: 'G0,4',
    value: QualityGradeTypes.G0_4,
  },
  {
    name: 'G1',
    value: QualityGradeTypes.G1,
  },
  {
    name: 'G1,6',
    value: QualityGradeTypes.G1_6,
  },
  {
    name: 'G2,5',
    value: QualityGradeTypes.G2_5,
  },
  {
    name: 'G6,3',
    value: QualityGradeTypes.G6_3,
  },
  {
    name: 'G16',
    value: QualityGradeTypes.G16,
  },
  {
    name: 'G40',
    value: QualityGradeTypes.G40,
  },
  {
    name: 'G100',
    value: QualityGradeTypes.G100,
  },
  {
    name: 'G250',
    value: QualityGradeTypes.G250,
  },
  {
    name: 'G630',
    value: QualityGradeTypes.G630,
  },
  {
    name: 'G1600',
    value: QualityGradeTypes.G1600,
  },
  {
    name: 'G4000',
    value: QualityGradeTypes.G4000,
  },
];
